import { MAP_ICON_URL } from '@/constants/icons';
import { ableToColorize } from './colorValidators';

const LIGHT_ICON_FILLS = ['white', '#ffffff', '#fff'];
const DESTINATION_ICON_SIZE = { width: 48, height: 68 } as const;
const WALKING_PERSON_ICON_SIZE = { width: 66, height: 66 } as const;

type ProcessSvgDataOptions = {
    preserveColors?: string[];
};

const shouldPreserveColor = (value: string, preserveColors: Set<string>) => {
    return preserveColors.has(value.trim().toLowerCase());
};

const isLightIconColor = (color: string) => {
    const normalizedColor = color.trim().toLowerCase();
    return normalizedColor === '#ffffff' || normalizedColor === '#fff' || normalizedColor === 'white';
};

export function colorizeSvgText(svgText: string, color: string, options?: ProcessSvgDataOptions): string {
    const appliedColor = ableToColorize(color) ? color : '#000000';
    const preserveColors = new Set((options?.preserveColors ?? []).map((value) => value.trim().toLowerCase()));
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');

    const walker = document.createTreeWalker(doc, NodeFilter.SHOW_ELEMENT, null);
    let node: Element | null = walker.nextNode() as Element;

    while (node) {
        const fillAttr = node.getAttribute('fill');
        const strokeAttr = node.getAttribute('stroke');

        if (fillAttr && ableToColorize(fillAttr) && !shouldPreserveColor(fillAttr, preserveColors)) {
            node.setAttribute('fill', appliedColor);
        }
        if (strokeAttr && ableToColorize(strokeAttr) && !shouldPreserveColor(strokeAttr, preserveColors)) {
            node.setAttribute('stroke', appliedColor);
        }

        node = walker.nextNode() as Element;
    }

    return new XMLSerializer().serializeToString(doc.documentElement);
}

export function processSvgData(svgText: string, color?: string, options?: ProcessSvgDataOptions): string {
    if (!color) return svgText;

    const svgAsXML = colorizeSvgText(svgText, color, options);
    return `data:image/svg+xml;base64,${btoa(svgAsXML)}`;
}

const svgTemplateCache = new Map<string, string>();

const loadSvgTemplate = async (iconUrl: string): Promise<string> => {
    const cachedTemplate = svgTemplateCache.get(iconUrl);
    if (cachedTemplate) {
        return cachedTemplate;
    }

    const response = await fetch(iconUrl);
    const svgTemplate = await response.text();
    svgTemplateCache.set(iconUrl, svgTemplate);
    return svgTemplate;
};

const loadImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load destination icon image'));
        image.src = url;
    });

const rasterizeSvgTextToIconUrl = async (
    svgText: string,
    iconSize: { width: number; height: number },
    fallbackIconUrl: string
): Promise<string> => {
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const svgObjectUrl = URL.createObjectURL(svgBlob);

    try {
        const image = await loadImage(svgObjectUrl);
        const canvas = document.createElement('canvas');
        canvas.width = iconSize.width;
        canvas.height = iconSize.height;

        const context = canvas.getContext('2d');
        if (!context) {
            return fallbackIconUrl;
        }

        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        const pngDataUrl = canvas.toDataURL('image/png');
        if (!pngDataUrl.startsWith('data:image/png')) {
            return fallbackIconUrl;
        }

        return pngDataUrl;
    } catch {
        return fallbackIconUrl;
    } finally {
        URL.revokeObjectURL(svgObjectUrl);
    }
};

const createThemedMarkerIconUrl = async (
    iconUrl: string,
    color: string,
    iconSize: { width: number; height: number }
): Promise<string> => {
    const svgTemplate = await loadSvgTemplate(iconUrl);
    const coloredSvg = colorizeSvgText(
        svgTemplate,
        color,
        isLightIconColor(color) ? undefined : { preserveColors: LIGHT_ICON_FILLS }
    );

    return rasterizeSvgTextToIconUrl(coloredSvg, iconSize, iconUrl);
};

export const createDestinationPointIconUrl = async (color: string): Promise<string> => {
    return createThemedMarkerIconUrl(MAP_ICON_URL.DESTINATION_POINT_ICON, color, DESTINATION_ICON_SIZE);
};

export const createWalkingPersonIconUrl = async (color: string): Promise<string> => {
    return createThemedMarkerIconUrl(MAP_ICON_URL.WALKING_PERSON_ICON, color, WALKING_PERSON_ICON_SIZE);
};
